import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Calendar,
  List,
  Eye,
  Filter,
  Search,
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function CustomerDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create-schedule");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showScheduleDetails, setShowScheduleDetails] = useState(false);

  // Sample Categories and Sub Categories
  const categories = [
    { id: "CAT-001", name: "Electronics", subCategories: ["Mobiles", "Laptops", "Accessories"] },
    { id: "CAT-002", name: "Machinery", subCategories: ["Industrial", "Automation", "Tools"] }
  ];

  // Sample Groups
  const groups = [
    { id: "GRP-001", name: "Premium Customers" },
    { id: "GRP-002", name: "Regular Customers" }
  ];

  // Sample Customers (for "bought from customer created")
  const customers = [
    { id: "CUST-001", name: "ABC Corp" },
    { id: "CUST-002", name: "XYZ Ltd" }
  ];

  // Sample Products
  const products = [
    { id: "PROD-001", name: "Smartphone X", category: "Electronics", subCategory: "Mobiles", price: "₹25,000" },
    { id: "PROD-002", name: "Laptop Pro", category: "Electronics", subCategory: "Laptops", price: "₹65,000" },
    { id: "PROD-003", name: "Industrial Drill", category: "Machinery", subCategory: "Industrial", price: "₹15,000" }
  ];

  // Sample Stock Data
  const [stockData] = useState({
    finishedGoods: [
      { product: "Smartphone X", stock: 75 },
      { product: "Laptop Pro", stock: 40 },
      { product: "Industrial Drill", stock: 30 }
    ],
    productionPending: [
      { product: "Smartphone X", pending: 25 },
      { product: "Laptop Pro", pending: 10 }
    ],
    outsourcePending: [
      { product: "Accessories", pending: 100 }
    ],
    rawMaterial: [
      { product: "Raw Material A", stock: 500 },
      { product: "Raw Material B", stock: 300 }
    ]
  });

  // Sample Schedules Data
  const [schedules, setSchedules] = useState([
    {
      id: "SCH-001",
      category: "Electronics",
      subCategory: "Mobiles",
      group: "Premium Customers",
      boughtFrom: "ABC Corp",
      date: "2024-04-01",
      product: "Smartphone X",
      quantity: 50,
      frequency: "Weekly",
      month: "",
      validTill: "2024-12-31",
      status: "Active"
    },
    {
      id: "SCH-002",
      category: "Electronics",
      subCategory: "Laptops",
      group: "Regular Customers",
      boughtFrom: "XYZ Ltd",
      date: "2024-04-05",
      product: "Laptop Pro",
      quantity: 25,
      frequency: "Monthly",
      month: "1st week",
      validTill: "2024-12-31",
      status: "Active"
    }
  ]);

  // Form state for Create Schedule
  const [formData, setFormData] = useState({
    category: "",
    subCategory: "",
    group: "",
    boughtFrom: "",
    date: "",
    product: "",
    quantity: "",
    frequency: "Weekly",
    month: "",
    validTill: ""
  });

  // Sidebar menu items
  const tabItems = [
    { id: "create-schedule", label: "Create Schedule", icon: <Calendar size={18} /> },
    { id: "list-schedule", label: "List Schedule", icon: <List size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle category change to update subcategories
  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    setFormData({ ...formData, category: categoryName, subCategory: "" });
  };

  // Handle product selection
  const handleProductChange = (e) => {
    const productName = e.target.value;
    setFormData({ ...formData, product: productName });
  };

  // Handle schedule submission
  const handleSubmitSchedule = (e) => {
    e.preventDefault();
    const newSchedule = {
      id: `SCH-${String(schedules.length + 1).padStart(3, '0')}`,
      ...formData,
      status: "Active"
    };
    setSchedules([...schedules, newSchedule]);
    alert("Schedule created successfully!");
    setFormData({
      category: "",
      subCategory: "",
      group: "",
      boughtFrom: "",
      date: "",
      product: "",
      quantity: "",
      frequency: "Weekly",
      month: "",
      validTill: ""
    });
  };

  // Handle view schedule details
  const handleViewSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setShowScheduleDetails(true);
  };

  // Filter function
  const filteredSchedules = () => {
    if (!searchTerm) return schedules;
    return schedules.filter(schedule =>
      schedule.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.subCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.group.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.product.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Create Schedule Component
  const CreateScheduleContent = () => (
    <div className="content-container">
      <form className="schedule-form" onSubmit={handleSubmitSchedule}>
        <div className="form-grid">
          <div className="form-group">
            <label>Category *</label>
            <select name="category" className="form-input" value={formData.category} onChange={handleCategoryChange} required>
              <option value="">Select Category</option>
              {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Sub Category *</label>
            <select name="subCategory" className="form-input" value={formData.subCategory} onChange={handleInputChange} required disabled={!formData.category}>
              <option value="">Select Sub Category</option>
              {formData.category && categories.find(c => c.name === formData.category)?.subCategories.map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Group *</label>
            <select name="group" className="form-input" value={formData.group} onChange={handleInputChange} required>
              <option value="">Select Group</option>
              {groups.map(group => <option key={group.id} value={group.name}>{group.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Bought from Customer *</label>
            <select name="boughtFrom" className="form-input" value={formData.boughtFrom} onChange={handleInputChange} required>
              <option value="">Select Customer</option>
              {customers.map(cust => <option key={cust.id} value={cust.name}>{cust.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Date *</label>
            <input type="date" name="date" className="form-input" value={formData.date} onChange={handleInputChange} required />
          </div>

          <div className="form-group">
            <label>Product Name *</label>
            <select name="product" className="form-input" value={formData.product} onChange={handleProductChange} required>
              <option value="">Select Product</option>
              {products.map(product => <option key={product.id} value={product.name}>{product.name} - {product.price}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>No of Quantity *</label>
            <input type="number" name="quantity" className="form-input" value={formData.quantity} onChange={handleInputChange} placeholder="Enter quantity" required />
          </div>

          <div className="form-group">
            <label>Frequency *</label>
            <select name="frequency" className="form-input" value={formData.frequency} onChange={handleInputChange}>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>

          {formData.frequency === "Monthly" && (
            <div className="form-group">
              <label>Month (Week)</label>
              <input type="text" name="month" className="form-input" value={formData.month} onChange={handleInputChange} placeholder="e.g., 1st week" />
            </div>
          )}

          <div className="form-group">
            <label>Valid Till Date *</label>
            <input type="date" name="validTill" className="form-input" value={formData.validTill} onChange={handleInputChange} required />
          </div>
        </div>

        <button type="submit" className="btn-submit">Submit Schedule</button>
      </form>
    </div>
  );

  // List Schedule Component
  const ListScheduleContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search schedules..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)}>
          <Filter size={16} /> Filter
        </button>
      </div>

      {filterOpen && (
        <div className="filter-panel">
          <select className="filter-select"><option value="">All Categories</option>{categories.map(c => <option key={c.id}>{c.name}</option>)}</select>
          <select className="filter-select"><option value="">All Groups</option>{groups.map(g => <option key={g.id}>{g.name}</option>)}</select>
          <input type="date" className="filter-input" placeholder="From Date" />
          <input type="date" className="filter-input" placeholder="To Date" />
          <button className="apply-filter">Apply</button>
        </div>
      )}

      <div className="schedule-list">
        {filteredSchedules().map(schedule => (
          <div key={schedule.id} className="schedule-card" onClick={() => handleViewSchedule(schedule)}>
            <div className="schedule-header">
              <h3>{schedule.product}</h3>
              <span className="status approved">{schedule.status}</span>
            </div>
            <div className="schedule-info">
              <p><strong>Category:</strong> {schedule.category} → {schedule.subCategory}</p>
              <p><strong>Group:</strong> {schedule.group} | <strong>Bought from:</strong> {schedule.boughtFrom}</p>
              <p><strong>Date:</strong> {schedule.date} | <strong>Quantity:</strong> {schedule.quantity}</p>
              <p><strong>Frequency:</strong> {schedule.frequency} {schedule.month && `- ${schedule.month}`} | <strong>Valid Till:</strong> {schedule.validTill}</p>
            </div>
            <button className="action-btn view" onClick={(e) => { e.stopPropagation(); handleViewSchedule(schedule); }}>
              <Eye size={16} /> View Details
            </button>
          </div>
        ))}
      </div>

      {/* Schedule Details Modal with Stock Information */}
      {showScheduleDetails && selectedSchedule && (
        <div className="modal-overlay" onClick={() => setShowScheduleDetails(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule Details - {selectedSchedule.product}</h2>
              <button className="modal-close" onClick={() => setShowScheduleDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item"><label>Category:</label><span>{selectedSchedule.category}</span></div>
                <div className="detail-item"><label>Sub Category:</label><span>{selectedSchedule.subCategory}</span></div>
                <div className="detail-item"><label>Group:</label><span>{selectedSchedule.group}</span></div>
                <div className="detail-item"><label>Bought from Customer:</label><span>{selectedSchedule.boughtFrom}</span></div>
                <div className="detail-item"><label>Date:</label><span>{selectedSchedule.date}</span></div>
                <div className="detail-item"><label>Product Name:</label><span>{selectedSchedule.product}</span></div>
                <div className="detail-item"><label>No of Quantity:</label><span>{selectedSchedule.quantity}</span></div>
                <div className="detail-item"><label>Frequency:</label><span>{selectedSchedule.frequency} {selectedSchedule.month && `- ${selectedSchedule.month}`}</span></div>
                <div className="detail-item"><label>Valid Till Date:</label><span>{selectedSchedule.validTill}</span></div>
              </div>

              <div className="stock-info">
                <h3>Stock Information</h3>
                <div className="stock-grid">
                  <div className="stock-card">
                    <h4>Finished Goods stock</h4>
                    {stockData.finishedGoods.map(s => (
                      <p key={s.product}>{s.product} → {s.stock}</p>
                    ))}
                  </div>
                  <div className="stock-card">
                    <h4>Production Pending Stock</h4>
                    {stockData.productionPending.map(s => (
                      <p key={s.product}>{s.product} → {s.pending}</p>
                    ))}
                  </div>
                  <div className="stock-card">
                    <h4>Outsource Pending Stock</h4>
                    {stockData.outsourcePending.map(s => (
                      <p key={s.product}>{s.product} → {s.pending}</p>
                    ))}
                  </div>
                  <div className="stock-card">
                    <h4>Raw Material</h4>
                    {stockData.rawMaterial.map(s => (
                      <p key={s.product}>{s.product} → {s.stock}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowScheduleDetails(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "create-schedule":
        return <CreateScheduleContent />;
      case "list-schedule":
        return <ListScheduleContent />;
      default:
        return <CreateScheduleContent />;
    }
  };

  return (
    <div className="customer-dashboard-container">
      {/* Sidebar */}
      <div className="customer-sidebar">
        <div className="customer-logo-container">
          <div className="customer-logo-icon">
            <img 
              src="/rahulerp.jpg" 
              alt="Company Logo" 
              className="customer-logo-image"
            />
          </div>
          <div>
            <h2 className="customer-company-name">Ragul Auto Tech</h2>
            <p className="customer-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="customer-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`customer-nav-item ${activeTab === tab.id ? 'customer-nav-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="customer-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="customer-logout-container">
          <button onClick={handleLogoutClick} className="customer-logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="customer-main-content">
        <header className="customer-header">
          <div>
            <h1 className="customer-main-title">Customer Dashboard</h1>
            <div className="customer-subtitle">
              Welcome, {user?.role || 'Customer'}
            </div>
          </div>
          <div className="customer-header-actions">
            <div className="customer-last-updated">Last updated: {new Date().toLocaleString()}</div>
          </div>
        </header>
        <BackButton />

        <div className="customer-content-card">
          {renderContent()}
        </div>

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Customer</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}